import type { ConversationAgent } from './conversation.type'

export const ClientPlatform = {
  web: 'web',
  mobileApp: 'mobile-app',
} as const
export type ClientPlatform = (typeof ClientPlatform)[keyof typeof ClientPlatform]

export const SalesAgentScene = {
  default: 'default',
  orderDetail: 'order-detail',
  salesrepDashboard: 'salesrep-dashboard',
  managerDashboard: 'manager-dashboard',
} as const
export type SalesAgentScene = (typeof SalesAgentScene)[keyof typeof SalesAgentScene]

export type SalesAgentDashboardScene =
  typeof SalesAgentScene.salesrepDashboard | typeof SalesAgentScene.managerDashboard

export const salesAgentDashboardSceneLabels: Record<SalesAgentDashboardScene, string> = {
  [SalesAgentScene.salesrepDashboard]: 'Salesrep Dashboard',
  [SalesAgentScene.managerDashboard]: 'Manager Dashboard',
}

export const MaguchiAgentScene = {
  salesMatrix: 'sales-matrix',
  salesMatrixSettings: 'sales-matrix-settings',
  meetingBoard: 'meeting-board',
  customerOrders: 'orders',
  customerNotes: 'notes',
  customerProfile: 'profile',
} as const
export type MaguchiAgentScene = (typeof MaguchiAgentScene)[keyof typeof MaguchiAgentScene]

export const maguchiAgentScenesWithFocusedMaguchi: MaguchiAgentScene[] = [
  MaguchiAgentScene.salesMatrix,
  MaguchiAgentScene.salesMatrixSettings,
  MaguchiAgentScene.meetingBoard,
]

export const MaguchiAgentCommandName = {
  setup: 'setup',
  preMtgBriefing: 'pre-mtg-briefing',
  postMtgBriefing: 'post-mtg-briefing',
  actionReport: 'action-report',
} as const
export type MaguchiAgentCommandName =
  (typeof MaguchiAgentCommandName)[keyof typeof MaguchiAgentCommandName]

export type Scene = SalesAgentScene | MaguchiAgentScene
export type CommandName = MaguchiAgentCommandName

export type SceneContextBase = {
  agent: ConversationAgent
  platform: ClientPlatform
  scene: Scene
  commandName?: CommandName
}

export type SalesAgentSceneContext = SceneContextBase & {
  agent: 'salesAgent'
  scene: SalesAgentScene
  commandName?: never
  // scene === 'order-detail' のときに必須。OrderTicket.id。
  focusedOrderId?: number
  // scene === 'salesrep-dashboard' のときに必須。エージェントが代わりに動く担当
  focusedSalesrepNumber?: number
}

export type MaguchiAgentSceneContext = SceneContextBase & {
  agent: 'maguchiAgent'
  scene: MaguchiAgentScene
  commandName?: MaguchiAgentCommandName
  customerId: number
  // maguchiAgentScenesWithFocusedMaguchi のシーンのときのみ意味を持つ。
  focusedMaguchiCategoryCode?: number
}

export type SceneContext = SalesAgentSceneContext | MaguchiAgentSceneContext

export const buildSalesAgentSceneContext = (
  input: {
    scene?: SalesAgentScene
    focusedOrderId?: number
    focusedSalesrepNumber?: number
  } = {},
): SalesAgentSceneContext => ({
  agent: 'salesAgent',
  platform: ClientPlatform.web,
  scene: input.scene ?? SalesAgentScene.default,
  focusedOrderId: input.focusedOrderId,
  focusedSalesrepNumber: input.focusedSalesrepNumber,
})

export const buildMaguchiAgentSceneContext = (input: {
  customerId: number
  scene: MaguchiAgentScene
  commandName?: MaguchiAgentCommandName
  focusedMaguchiCategoryCode?: number
}): MaguchiAgentSceneContext => ({
  agent: 'maguchiAgent',
  platform: ClientPlatform.web,
  scene: input.scene,
  commandName: input.commandName,
  customerId: input.customerId,
  focusedMaguchiCategoryCode: input.focusedMaguchiCategoryCode,
})
